import { ContentListShell } from "@/components/studio/content-list-shell";
import { LifecycleActionsMenu } from "@/components/studio/lifecycle-actions-menu";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { categoryService } from "@/lib/api/categories.service";
import {
  CONTENT_JOURNEY,
  ContentJourneyService,
} from "@/lib/api/journey.service";
import { Category, StudioContentStatus } from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  formatDate,
  STATUS_CONFIG,
} from "@/lib/studio-constants";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Map } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

const journeyConfig = CONTENT_TYPE_CONFIG.journey;
const PAGE_SIZE = 10;

interface JourneyListItem {
  id: string;
  title: string;
  description: string | null;
  totalDays: number | null;
  estimatedDailyMinutes: number | null;
  difficultyLevel: string | null;
  progressionType: string | null;
  status: StudioContentStatus;
  version: number;
  updatedAt: string;
  createdAt: string;
  ownerId: string;
}

export default function JourneyListPage() {
  const [, setLocation] = useLocation();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    status: "all",
    category: "all",
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const statusParam =
    filterValues.status !== "all"
      ? (filterValues.status as StudioContentStatus)
      : undefined;
  const categoryParam =
    filterValues.category !== "all" ? filterValues.category : undefined;
  const searchParam = searchQuery.trim() || undefined;

  const { data, isLoading } = useQuery({
    queryKey: [
      CONTENT_JOURNEY,
      "list",
      {
        page,
        limit: PAGE_SIZE,
        search: searchParam,
        status: statusParam,
        categoryId: categoryParam,
      },
    ],
    queryFn: () =>
      ContentJourneyService.findAll({
        page,
        limit: PAGE_SIZE,
        search: searchParam,
        status: statusParam,
        categoryId: categoryParam,
      }),
    placeholderData: keepPreviousData,
  });

  const journeyItems: JourneyListItem[] = (data?.items ?? []).map((row) => ({
    id: row.id,
    title: row.title,
    description: row.description,
    totalDays: row.totalDays,
    estimatedDailyMinutes: row.estimatedDailyMinutes ?? null,
    difficultyLevel: row.difficultyLevel ?? null,
    progressionType: row.progressionType ?? null,
    status: row.status,
    version: row.version,
    updatedAt: row.updatedAt,
    createdAt: row.createdAt,
    ownerId: row.ownerId ?? "",
  }));

  const totalItems = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;
  const startIndex = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + journeyItems.length, totalItems);

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { submitMutation, archiveMutation, withdrawMutation } =
    useContentLifecycle({
      contentType: "journey",
      apiPath: CONTENT_JOURNEY,
      listRoute: "/studio/journeys",
      editRoute: (id) => `/studio/journeys/${id}/edit`,
    });

  const filtered = journeyItems;

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const customFilters = [
    {
      key: "category",
      label: "Categories",
      options: categories.map((c) => ({ value: c.id, label: c.title })),
    },
  ];

  const difficultyLabel = (level: string | null) => {
    if (!level) return "—";
    const labels: Record<string, string> = {
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
      deep_work: "Deep Work",
    };
    return labels[level] || level;
  };

  const progressionLabel = (type: string | null) => {
    if (!type) return "—";
    const labels: Record<string, string> = {
      sequential: "Sequential",
      flexible: "Flexible",
    };
    return labels[type] || type;
  };

  return (
    <ContentListShell
      contentTypeConfig={journeyConfig}
      isLoading={isLoading}
      itemCount={filtered.length}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      filters={customFilters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      showLanguageFilter={false}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        startIndex,
        endIndex,
        canGoNext: page < totalPages,
        canGoPrev: page > 1,
        onPageChange: setPage,
        onNext: () => setPage((p) => Math.min(p + 1, totalPages)),
        onPrev: () => setPage((p) => Math.max(p - 1, 1)),
      }}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Title</TableHead>
            <TableHead className="text-center">Total Days</TableHead>
            <TableHead>Difficulty</TableHead>
            <TableHead>Progression</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((item) => {
            const statusConfig = STATUS_CONFIG[item.status];
            return (
              <TableRow
                key={item.id}
                className="cursor-pointer"
                onClick={() => setLocation(`/studio/journeys/${item.id}/edit`)}
                data-testid={`row-journey-${item.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-muted shrink-0">
                      <Map className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="font-medium text-foreground truncate"
                        data-testid={`text-journey-title-${item.id}`}
                      >
                        {item.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        v{item.version}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className="text-sm text-muted-foreground"
                    data-testid={`text-journey-days-${item.id}`}
                  >
                    {item.totalDays ?? "—"}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    className="text-sm text-muted-foreground"
                    data-testid={`text-journey-difficulty-${item.id}`}
                  >
                    {difficultyLabel(item.difficultyLevel)}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    className="text-sm text-muted-foreground"
                    data-testid={`text-journey-progression-${item.id}`}
                  >
                    {progressionLabel(item.progressionType)}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={statusConfig?.variant ?? "secondary"}
                    className="no-default-hover-elevate no-default-active-elevate"
                    data-testid={`badge-journey-status-${item.id}`}
                  >
                    {statusConfig?.label ?? item.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span
                    className="text-sm text-muted-foreground"
                    data-testid={`text-journey-updated-${item.id}`}
                  >
                    {formatDate(item.updatedAt)}
                  </span>
                </TableCell>
                <TableCell>
                  <LifecycleActionsMenu
                    itemId={item.id}
                    status={item.status}
                    createdBy={item.ownerId}
                    onEdit={(id) => setLocation(`/studio/journeys/${id}/edit`)}
                    onSubmit={(id) => submitMutation.mutate(id)}
                    onArchive={(id) => archiveMutation.mutate(id)}
                    onWithdraw={(id) => withdrawMutation.mutate(id)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ContentListShell>
  );
}
